import { Link, useOutletContext, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api, hlaskaZChyby } from '../../api/client.js';
import type { Vykres, VykresDetail } from '../../api/types.js';
import type { TerenKontext } from '../../components/TerenLayout.js';
import { VykresViewer } from '../../components/VykresViewer.js';
import { Badge, Chyba, Nacitani } from '../../components/ui.js';
import { formatDen } from '../../lib/format.js';
import { jeOnline } from '../../lib/offline.js';

/**
 * Terén — Výkresy (wireframe 13 §1, spodní lišta): seznam výkresů zakázky
 * s badge platné verze; ťuknutí otevře viewer (VykresViewer — PDF s piny,
 * DWG ke stažení). Mistr si výkres otevírá často — proto přímo v liště.
 */
export function VykresyPage() {
  const { zakazka } = useOutletContext<TerenKontext>();

  const {
    data: vykresy,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['vykresy', zakazka.id],
    queryFn: () => api.get<Vykres[]>(`/api/drawings?zakazkaId=${encodeURIComponent(zakazka.id)}`),
  });

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-bold">Výkresy</h1>
      {isLoading && <Nacitani text="Načítám výkresy…" />}
      {error ? (
        jeOnline() ? (
          <Chyba>{hlaskaZChyby(error)}</Chyba>
        ) : (
          <p className="text-base text-text-secondary">
            Seznam výkresů není dostupný offline — připojte se k internetu.
          </p>
        )
      ) : null}
      {vykresy && vykresy.length === 0 && (
        <p className="text-base text-text-secondary">
          Zakázka zatím nemá výkresy — nahrává je kancelář.
        </p>
      )}
      <ul className="flex flex-col gap-3">
        {(vykresy ?? []).map((v) => (
          <li key={v.id}>
            <Link
              to={`/teren/vykresy/${v.id}`}
              className="flex min-h-14 items-center justify-between gap-3 rounded-xl border border-border bg-surface p-4 hover:bg-bg"
            >
              <span className="min-w-0">
                <span className="block text-lg font-bold">{v.cislo}</span>
                <span className="block truncate text-base text-text-secondary">{v.nazev}</span>
              </span>
              <span className="flex shrink-0 flex-col items-end gap-1">
                {v.platnaVerze ? (
                  <Badge stav="success">
                    platná v{v.platnaVerze.verze} ·{' '}
                    {formatDen(v.platnaVerze.datum ?? v.platnaVerze.createdAt)}
                  </Badge>
                ) : (
                  <Badge stav="neutral">bez verze</Badge>
                )}
                {v.format !== 'PDF' && <Badge stav="neutral">{v.format}</Badge>}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Detail výkresu v terénu — viewer přes celou šířku, návrat na seznam. */
export function VykresTerenDetailPage() {
  const { id = '' } = useParams();

  const {
    data: vykres,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['vykres', id],
    queryFn: () => api.get<VykresDetail>(`/api/drawings/${id}`),
  });

  return (
    <div className="flex flex-col gap-3">
      <Link
        to="/teren/vykresy"
        className="inline-flex min-h-12 items-center gap-1 self-start text-base font-semibold text-primary underline underline-offset-2"
      >
        ← Výkresy
      </Link>
      {isLoading && <Nacitani text="Načítám výkres…" />}
      {error ? <Chyba>{hlaskaZChyby(error)}</Chyba> : null}
      {vykres && (
        <>
          <h1 className="text-xl font-bold">
            {vykres.cislo} — {vykres.nazev}
          </h1>
          <VykresViewer vykres={vykres} />
        </>
      )}
    </div>
  );
}
